import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaHourglass } from 'react-icons/fa';
import PageHeader from "../components/PageHeader";
import Footer from "../components/Footer";
import BadgesSection from '../components/BadgesSection';
import CartDrawer from '../components/CartDrawer';
import registrationBgImage from "/pages-bg/8.png";
import apiService from '../services/apiService';
import { useCart } from '../contexts/CartContext';

const RegistrationPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showFeaturesComparison, setShowFeaturesComparison] = useState(null);
  const [currency, setCurrency] = useState({ code: 'USD', symbol: '$' });
  const [exchangeRate, setExchangeRate] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [subscriptionSuccess, setSubscriptionSuccess] = useState("");
  
  // Access cart functionality from context
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchUserLocationAndExchangeRate = async () => {
      try {
        const locationResponse = await fetch('https://ipapi.co/json/');
        const locationData = await locationResponse.json();
        
        let currencyCode = 'USD';
        let currencySymbol = '$';

        if (locationData.country_code === 'NG') {
          currencyCode = 'NGN';
          currencySymbol = '₦';
        } else if (locationData.country_code === 'GB') {
          currencyCode = 'GBP';
          currencySymbol = '£';
        } else if (locationData.currency === 'EUR') {
          currencyCode = 'EUR';
          currencySymbol = '€';
        }

        setCurrency({ code: currencyCode, symbol: currencySymbol });

        if (currencyCode !== 'USD') {
          const rateResponse = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
          const rateData = await rateResponse.json();
          setExchangeRate(rateData.rates[currencyCode]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load exchange rates. Using default conversions.');
        setLoading(false);
      }
    };

    fetchUserLocationAndExchangeRate();
  }, []);

  const convertCurrency = (priceUSD) => {
    if (loading) return 'Loading...';
    if (error) return `${currency.symbol}${priceUSD.toLocaleString(undefined, {maximumFractionDigits: 2})}`;
    const convertedPrice = priceUSD * exchangeRate;
    return `${currency.symbol}${convertedPrice.toLocaleString(undefined, {maximumFractionDigits: 2})}`;
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const toggleFeaturesComparison = (category) => {
    setShowFeaturesComparison(prevState => prevState === category ? null : category);
  };

  const handleBadgeSelection = (badge) => {
    // Use the addToCart from CartContext
    addToCart(badge);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setSubscriptionError("Please enter your name");
      return;
    }
    
    if (!email.trim() || !email.includes('@')) {
      setSubscriptionError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    setSubscriptionError("");
    setSubscriptionSuccess("");

    try {
      const newsletterData = {
        email: email.trim(),
        name: name.trim(),
        listId: 5  // Always subscribe to e-comms list ID 5
      };
      
      const response = await apiService.forms.newsletter(newsletterData);
      
      if (response.data && response.data.success) {
        setSubscriptionSuccess(response.data.message || "You're In! We'll keep you updated!");
        setEmail("");
        setName("");
      } else {
        setSubscriptionError(response.data?.message || "Subscription failed. Please try again later.");
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      if (error.response) {
        // Handle specific error responses
        switch (error.response.status) {
          case 409:
            setSubscriptionError("This email is already subscribed to our newsletter.");
            break;
          case 400:
            setSubscriptionError("Please enter a valid email address.");
            break;
          default:
            setSubscriptionError(error.response.data?.message || "Subscription failed. Please try again later.");
        }
      } else if (error.request) {
        setSubscriptionError("Unable to reach our servers. Please check your internet connection.");
      } else {
        setSubscriptionError("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const attendeeCategories = [
    {
      title: "FINANCIERS",
      items: ["Venture Capitalists", "Angel Investors", "Film Financing Institutions", "Financial Institutions", "Brands that Support Films", "Investors from Other Industries", "Private Equity Firms", "Government Agencies and Grants Bodies", "Film and Media Investment Funds"]
    },
    {
      title: "INDUSTRY PROFESSIONALS",
      items: ["Film Producers", "Film Directors", "Screenwriters", "Film Distributors", "Film Sales Agents", "Film Production Companies", "Film Marketing and PR Professionals", "Film Exhibitors and Theaters", "Cinematographers", "Film Editors", "Sound Designers", "Visual Effects Artists"]
    },
    {
      title: "INDUSTRY STAKEHOLDER & ENTHUSIASTS",
      items: ["Film and Media Associations", "Film Studios", "Media Companies", "Creative Agencies", "Technology Providers for Film Production", "Journalists and Film Critics", "Film Bloggers and Influencers", "Media Outlets (TV, Radio, Online Platforms)", "Film Schools and Universities", "Policy Makers in Media and Culture", "Film Commissions Representatives", "Film Enthusiasts", "Students and Academics in Film Studies"]
    }
  ];

  const faqItems = [
    {
      question: "Registration",
      answer: (
        <>
          <p>Once you complete your registration to AFFF, you will receive a confirmation email containing a registration number. Keep this email handy as you may need it to make changes to your registration. You can also easily purchase all the passes you need in a single order or register for AFFF even if you don't know yet who from your team will be attending.</p>
          <ul className="list-disc pl-5 mt-2">
            <li><a href="#" className="text-custom-red hover:underline">Find your registration number</a></li>
            <li><a href="#" className="text-custom-red hover:underline">Registering a group</a></li>
            <li><a href="#" className="text-custom-red hover:underline">Registering with placeholder attendees</a></li>
          </ul>
        </>
      )
    },
    {
      question: "Transferring and canceling a pass",
      answer: (
        <>
          <p>We understand life happens. You can transfer your Africa Film Finance Forum pass to someone at any time prior to the event. You can get a transfer your pass until November 10, 2024. After November 10, 2024, we will won't be able to provide help with pass transfers.</p>
          <ul className="list-disc pl-5 mt-2">
            <li><a href="#" className="text-custom-red hover:underline" target="_blank">Transfer your pass</a></li>
          </ul>
        </>
      )
    },
    {
      question: "Need help with your purchase?",
      answer: (
        <>
          <p>If you need help with your purchase or are eligible for a discount, just get in touch with us by replying to your registration confirmation email or emailing us at help@africafilmfinanceforum.com. We will be happy to assist you.</p>
          <a href="#" className="text-custom-red hover:underline" target="_blank">Are you eligible for a discount?</a>
        </>
      )
    }
  ];

  return (
    <div className="relative bg-[#FAF0E6]">
      <PageHeader 
        title="REGISTRATION" 
        description="Join the Africa Film Finance Forum and be part of the revolution in African cinema."
        backgroundImage={registrationBgImage}
      />
      <main>
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <section className="mb-16 text-[1.1rem] border-y-3 ">
            <div className="border-l-4 sm:border-l-8 border-[#A18749] pl-2 sm:pl-4 mb-4">
              <h2 className="font-header text-4xl sm:text-5xl font-bold mb-6 tracking-wider text-custom-base">DON'T MISS THE AFRICA FILM FINANCE FORUM: REGISTER NOW!</h2>
            </div>
            <p className="mb-4">
              The Africa Film Finance Forum (AFFF) is transforming African cinema by connecting visionary filmmakers with forward-thinking financiers. It fosters collaboration, growth, and success by uniting industry professionals and offering filmmakers the resources they need while providing financiers with high-potential projects. Join AFFF to be part of a revolution in African storytelling and seize the next big opportunity.            
            </p>
          </section>

          <section className="mb-16">
            <h2 className="font-header text-4xl sm:text-5xl font-bold mb-6 tracking-wider text-custom-base">Who Should Attend?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {attendeeCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-md shadow-md overflow-hidden border border-gray-300">
                  <h3 className="font-roboto text-2xl font-bold p-4 bg-gray-100 text-custom-base">{category.title}</h3>
                  <ul className="list-disc pl-8 pr-4 py-4 space-y-2 font-lexend">
                    {category.items.map((item, i) => (
                      <li key={i} className="text-sm sm:text-base">{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <BadgesSection 
            addToCart={handleBadgeSelection} 
            currency={currency}
            showFeaturesComparison={showFeaturesComparison}
            toggleFeaturesComparison={toggleFeaturesComparison}
            convertCurrency={convertCurrency}
          />

          <section className="w-full bg-white py-16">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-header text-5xl sm:text-5xl font-bold tracking-wider text-custom-base pb-6">REGISTRATION FAQS</h2>
                <Link to="/help-center" className="border-2 border-custom-base text-custom-base px-4 py-2 font-bold hover:bg-custom-base hover:text-white transition duration-300">
                  SUPPORT CENTER
                </Link>
              </div>
              <div className="max-w-4xl mx-auto mt-6">
                {faqItems.map((item, index) => (
                  <div key={index} className="mb-4 border border-[#FDF6EC]">
                    <button
                      className="w-full text-left p-4 bg-[#FDF6EC] hover:bg-[#FCF3E7] transition-colors duration-300 font-lexend font-bold flex justify-between items-center"
                      onClick={() => toggleFaq(index)}
                    >
                      <h3 className="text-lg sm:text-xl">{item.question}</h3>
                      <span className="text-xl">{expandedFaq === index ? '×' : '+'}</span>
                    </button>
                    {expandedFaq === index && (
                      <div className="p-6 bg-[#FDF6EC]">
                        <div className="text-base sm:text-lg">{item.answer}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-r from-[#f16764] to-[#FFA726] py-12">
            <div className="container mx-auto px-4 sm:px-6">
              <div>
                <h2 className="font-header text-4xl sm:text-5xl font-bold mb-4 text-white tracking-wider">STAY UPDATED ON AFFF ANNOUNCEMENTS</h2>
                <p className="font-lexend text-white text-xl mb-8">Don't miss out important AFFF updates and announcements. Join our 2024 mailing list</p>
              </div>
              <div>
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Enter Your Name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setSubscriptionError("");
                      setSubscriptionSuccess("");
                    }}
                    className="w-full sm:w-1/3 p-3 rounded-none font-lexend text-lg border-2 border-white bg-transparent text-white placeholder-white"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Enter Email Address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setSubscriptionError("");
                      setSubscriptionSuccess("");
                    }}
                    className="flex-grow p-3 rounded-none font-lexend text-lg border-2 border-white bg-transparent text-white placeholder-white"
                    required
                  />
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`bg-white text-custom-red px-8 py-3 font-bold hover:bg-gray-100 transition duration-300 font-lexend text-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-custom-red" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        JOINING...
                      </span>
                    ) : 'JOIN THE LIST'}
                  </button>
                </form>
                {subscriptionError && <p className="text-red-200 text-sm mb-2">{subscriptionError}</p>}
                {subscriptionSuccess && <p className="text-green-200 text-sm mb-2">{subscriptionSuccess}</p>}
              </div>
              <p className="font-lexend text-white text-sm">
                We're committed to your privacy. AFFF uses the information you provide to contact you about AFFF content and events. You may unsubscribe from these communications at any time. For more information, check out our privacy policy.
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Cart Drawer Component - this replaces the previous Cart modal */}
      <CartDrawer />

      <Footer />
    </div>
  );
};

export default RegistrationPage;